import { Button, type } from '@datahub/design-system'
import type { PortalContext } from '../lib/types'
import { PortalPage } from './PortalPage'

interface NoOpenRequestProps {
  context: PortalContext
  starting: boolean
  onStart: () => void
}

/**
 * Shown when the link is valid but Stears has no request open. The company is not
 * blocked: CYC-10 lets it start a voluntary update whenever it likes, which opens
 * a self-serve cycle so the form, validation and submission all behave normally.
 */
export function NoOpenRequest({ context, starting, onStart }: NoOpenRequestProps) {
  const { displayName } = context.company
  const contact = context.owner

  return (
    <PortalPage>
      <div className="flex items-center justify-center" style={{ minHeight: 'calc(100% - 44px)', padding: 48 }}>
        <div className="flex flex-col bg-background-main" style={{ border: '0.5px solid #4B5563', maxWidth: 620 }}>
          <div
            className="flex items-center bg-background-skeleton"
            style={{ height: 52, padding: '0 24px', borderBottom: '0.5px solid #4B5563' }}
          >
            <span className="font-mulish text-white" style={{ ...type.panelHeading, letterSpacing: '-0.16px' }}>
              No request open right now
            </span>
          </div>

          <div className="flex flex-col" style={{ padding: 24, gap: 20 }}>
            <h1 className="font-jakarta font-bold text-white" style={type.pageTitle}>
              Update {displayName} whenever you like
            </h1>

            <p className="font-mulish text-text-description" style={type.body}>
              Stears asks for an update on a set cadence, and there is no request open for you today. You do not have
              to wait for one. Start now and correct anything that has changed.
            </p>

            <p className="font-mulish text-text-description" style={type.body}>
              Keep this link. It is unique to {displayName}, it does not change, and it will work for every future
              update too.
            </p>

            <div>
              <Button variant="filled-grey" size="medium" onClick={onStart} disabled={starting} className="w-full">
                {starting ? 'Opening…' : 'Start an update'}
              </Button>
            </div>

            <p
              className="font-mulish text-text-support"
              style={{ ...type.body, borderTop: '0.5px solid #4B5563', paddingTop: 20 }}
            >
              Questions?{' '}
              {contact ? (
                <>
                  Email {contact.name} at{' '}
                  <a href={`mailto:${contact.email}`} className="underline" style={{ color: '#85BEFF' }}>
                    {contact.email}
                  </a>
                  .
                </>
              ) : (
                'Email the Stears data team.'
              )}
            </p>
          </div>
        </div>
      </div>
    </PortalPage>
  )
}
